import { localeFor } from '@/lib/locale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/contexts/SharedAuthContext';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ButtonColorful } from '@/components/ui/button-colorful';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import {
  Check,
  FileText,
  Image as ImageIcon,
  Languages,
  LayoutGrid,
  Link2,
  Loader2,
  Megaphone,
  Minus,
  Newspaper,
  Pencil,
  Plus,
  Settings2,
  Sparkles,
  Undo2,
  GalleryHorizontal,
  Users,
  Gauge,
  Clapperboard,
  Video,
} from 'lucide-react';
import { LandingBlocksGrid } from '@/components/people-landing/LandingBlocksGrid';
import { BlockEditorDialog } from '@/components/people-landing/BlockEditorDialog';
import { PagesManagerDialog } from '@/components/people-landing/PagesManagerDialog';
import { useLandingImages } from '@/components/people-landing/useLandingImages';
import type {
  LandingBlock,
  LandingBlockType,
  LandingLang,
} from '../../../../shared/types/people-landing';

const HOME_SLUG = 'home';

const BLOCK_TYPE_OPTIONS: { type: LandingBlockType; icon: React.ElementType }[] = [
  { type: 'hero', icon: Sparkles },
  { type: 'richtext', icon: FileText },
  { type: 'image', icon: ImageIcon },
  { type: 'cards', icon: LayoutGrid },
  { type: 'links', icon: Link2 },
  { type: 'embed', icon: Video },
  { type: 'callout', icon: Megaphone },
  { type: 'team', icon: Users },
  { type: 'progress', icon: Gauge },
  { type: 'news', icon: Newspaper },
  { type: 'video', icon: Clapperboard },
  { type: 'marquee', icon: GalleryHorizontal },
  { type: 'divider', icon: Minus },
];

export function PeopleLandingPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const utils = trpc.useUtils();

  const activeSlug = slug ?? HOME_SLUG;
  // El contenido sigue el selector de idioma de la plataforma (fallback a EN,
  // el idioma original del site, cuando la variante no existe todavía).
  const shortLang = i18n.language?.slice(0, 2);
  const uiLang: LandingLang = shortLang === 'es' || shortLang === 'pt' ? shortLang : 'en';

  const { data: nav, isLoading: navLoading } = trpc.peopleLanding.getNav.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  });
  const { data: page, isLoading: pageLoading } = trpc.peopleLanding.getPage.useQuery(
    { slug: activeSlug, lang: uiLang },
    { staleTime: 5 * 60 * 1000 }
  );

  const userRoles: string[] = user?.roles ?? [];
  const canEdit = userRoles.includes('people') || userRoles.includes('admin');

  const [editing, setEditing] = useState(false);
  const [editorState, setEditorState] = useState<{
    block: LandingBlock | null;
    createType?: LandingBlockType;
  } | null>(null);
  const [pagesOpen, setPagesOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LandingBlock | null>(null);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  // Borrador de layout (Fase 3): en modo edición, mover / redimensionar /
  // duplicar / borrar / editar cambian el borrador y se ven en vivo; se persiste
  // con "Guardar" (o al tocar "Listo"). Fuera de edición se muestra lo guardado.
  //
  // El borrador va ATADO a la página + idioma donde nació (`key`). Incidente
  // 15/09: en modo edición, tocar otra tab cambiaba `page` pero el borrador
  // seguía siendo el de la página anterior; "Guardar" lo escribía sobre la
  // página nueva (Learning terminó pisando el Home). Ahora un borrador solo se
  // puede guardar en su propia página, y al cambiar de tab en modo edición los
  // cambios pendientes se guardan donde corresponde y se sale de edición.
  const draftKey = `${activeSlug}@${uiLang}`;
  const [draft, setDraft] = useState<{ key: string; blocks: LandingBlock[] } | null>(null);
  const draftBlocks = draft && draft.key === draftKey ? draft.blocks : null;
  const setDraftBlocks = (next: LandingBlock[] | null) =>
    setDraft(next ? { key: draftKey, blocks: next } : null);
  useEffect(() => {
    if (!editing) { setDraft(null); savedInSessionRef.current = false; return; }
    if (!page) return; // la página nueva todavía carga: sin borrador no hay nada que guardar
    setDraft((d) =>
      d && d.key === draftKey ? d : { key: draftKey, blocks: page.blocks.map((b) => ({ ...b })) }
    );
  }, [editing, draftKey, page]);
  const shownBlocks: LandingBlock[] = editing && draftBlocks ? draftBlocks : (page?.blocks ?? []);
  const dirty = editing && !!draftBlocks && JSON.stringify(draftBlocks) !== JSON.stringify(page?.blocks ?? []);

  const imageUrls = useLandingImages(shownBlocks);

  // slug inexistente → home (recién cuando nav y page ya respondieron)
  useEffect(() => {
    if (navLoading || pageLoading) return;
    if (slug && !page && nav && !nav.pages.some((p) => p.slug === slug)) {
      navigate('/people/landing', { replace: true });
    }
  }, [navLoading, pageLoading, slug, page, nav, navigate]);

  const savePageMutation = trpc.peopleLanding.savePage.useMutation({
    onSuccess: () => utils.peopleLanding.getPage.invalidate(),
    onError: (e) => {
      toast.error(e.message);
      utils.peopleLanding.getPage.invalidate();
    },
  });

  const editingCreatesVariant = editing && page && page.lang !== uiLang;

  // Traduce lo que se está VIENDO (page.lang — puede ser el fallback EN) al
  // resto de los idiomas, secuencial (1 idioma por request, timeout CloudFront).
  const translateMutation = trpc.peopleLanding.translatePage.useMutation();
  const translateSource: LandingLang = page?.lang ?? uiLang;
  const translateTargets = (['es', 'en', 'pt'] as LandingLang[]).filter(
    (l) => l !== translateSource
  );

  const handleTranslate = async () => {
    if (!page) return;
    setTranslateOpen(false);
    setIsTranslating(true);
    try {
      for (const toLang of translateTargets) {
        await translateMutation.mutateAsync({
          slug: activeSlug,
          fromLang: translateSource,
          toLang,
        });
      }
      toast.success(
        t('people_landing.translate.done', 'Página traducida a {{langs}}', {
          langs: translateTargets.map((l) => l.toUpperCase()).join(' + '),
        })
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      // en finally: si un idioma se tradujo y el siguiente falló, el cache
      // igual debe refrescar lo que sí se escribió
      utils.peopleLanding.getPage.invalidate();
      utils.peopleLanding.getNav.invalidate();
      setIsTranslating(false);
    }
  };

  const createHomeMutation = trpc.peopleLanding.createPage.useMutation({
    onSuccess: () => {
      utils.peopleLanding.getNav.invalidate();
      utils.peopleLanding.getPage.invalidate();
      setEditing(true);
    },
    onError: (e) => toast.error(e.message),
  });

  // Traducción automática (17/09, pedido de Sol/Milagros): "la gente se olvida
  // de tocar Traducir". Al tocar "Listo" —o al cambiar de pestaña con cambios—
  // la página guardada se traduce sola a los otros dos idiomas, en el servidor.
  const savedInSessionRef = useRef(false);
  const invalidateTimersRef = useRef<number[]>([]);
  useEffect(() => () => { invalidateTimersRef.current.forEach((id) => window.clearTimeout(id)); }, []);
  const autoTranslateMutation = trpc.peopleLanding.translateAll.useMutation();
  // Resultado de la traducción en segundo plano: se consulta el estado que la
  // Lambda deja en ConfigTable hasta que termina y se avisa (éxito o fallo con
  // botón para reintentar). Antes el fallo quedaba solo en el log de la Lambda
  // y cada idioma podía quedar de una época distinta sin que nadie lo viera.
  const POLL_MS = 8_000;
  const POLL_MAX_MS = 6 * 60_000;
  const watchAutoTranslate = (slug: string, fromLang: LandingLang, startedAt: number, langs: string) => {
    const tick = async () => {
      let status: Awaited<ReturnType<typeof utils.peopleLanding.getTranslateStatus.fetch>> | null = null;
      try { status = await utils.peopleLanding.getTranslateStatus.fetch({ slug }); } catch { status = null; }
      const finished = status && (status.state === 'done' || status.state === 'failed') && (!status.finishedAt || Date.parse(status.finishedAt) >= startedAt - 60_000);
      if (finished && status) {
        utils.peopleLanding.getPage.invalidate();
        if (status.state === 'done') {
          toast.success(t('people_landing.translate.done', 'Página traducida a {{langs}}', { langs: status.done.map((l) => l.toUpperCase()).join(' + ') }));
        } else {
          const failedLangs = status.failed.map((f) => f.lang.toUpperCase()).join(' + ');
          toast.error(t('people_landing.translate.auto_failed_bg', 'No pudimos traducir a {{langs}}. Los otros idiomas quedaron como estaban.', { langs: failedLangs }), {
            duration: Infinity,
            action: { label: t('people_landing.translate.retry', 'Reintentar'), onClick: () => triggerAutoTranslate(slug, fromLang) },
          });
        }
        return;
      }
      if (Date.now() - startedAt > POLL_MAX_MS) {
        toast.error(t('people_landing.translate.auto_timeout', 'La traducción a {{langs}} está tardando más de lo normal. Revisá los otros idiomas o usá "Traducir".', { langs }), { duration: 15_000 });
        return;
      }
      invalidateTimersRef.current.push(window.setTimeout(tick, POLL_MS));
    };
    invalidateTimersRef.current.push(window.setTimeout(tick, POLL_MS));
  };
  const triggerAutoTranslate = (slug: string, fromLang: LandingLang) => {
    const langs = (['es', 'en', 'pt'] as LandingLang[]).filter((l) => l !== fromLang).map((l) => l.toUpperCase()).join(' + ');
    autoTranslateMutation.mutate(
      { slug, fromLang },
      {
        onSuccess: (r) => {
          if (r.queued) {
            toast.success(t('people_landing.translate.auto_queued', 'Guardado. Traduciendo a {{langs}} en segundo plano (1–2 min).', { langs }));
            watchAutoTranslate(slug, fromLang, Date.now(), langs);
          } else if (r.failed.length > 0) {
            const failedLangs = r.failed.map((f) => f.lang.toUpperCase()).join(' + ');
            utils.peopleLanding.getPage.invalidate();
            toast.error(t('people_landing.translate.auto_failed_bg', 'No pudimos traducir a {{langs}}. Los otros idiomas quedaron como estaban.', { langs: failedLangs }), {
              duration: Infinity,
              action: { label: t('people_landing.translate.retry', 'Reintentar'), onClick: () => triggerAutoTranslate(slug, fromLang) },
            });
          } else {
            toast.success(t('people_landing.translate.done', 'Página traducida a {{langs}}', { langs }));
            utils.peopleLanding.getPage.invalidate();
          }
        },
        onError: (e) => toast.error(t('people_landing.translate.auto_failed', 'Guardado, pero no pudimos traducir a {{langs}}: {{error}}. Usá "Traducir".', { langs, error: e.message }), {
          duration: Infinity,
          action: { label: t('people_landing.translate.retry', 'Reintentar'), onClick: () => triggerAutoTranslate(slug, fromLang) },
        }),
      }
    );
  };

  const saveBlocks = (blocks: LandingBlock[]) => {
    // Guard de a dónde se escribe: la página cargada tiene que ser la activa.
    // Con el fallback EN `page.slug` sigue siendo el slug pedido, así que esto
    // solo frena un desfasaje real entre lo que se ve y lo que se guardaría.
    if (!page || page.slug !== activeSlug) return;
    utils.peopleLanding.getPage.setData(
      { slug: activeSlug, lang: uiLang },
      { ...page, blocks, lang: uiLang }
    );
    savePageMutation.mutate(
      { slug: activeSlug, title: page.title, blocks, lang: uiLang },
      { onSuccess: () => { savedInSessionRef.current = true; } }
    );
  };
  /** Igual que saveBlocks pero espera al backend (para traducir recién cuando el guardado está). */
  const saveBlocksAsync = async (blocks: LandingBlock[]) => {
    if (!page || page.slug !== activeSlug) return false;
    utils.peopleLanding.getPage.setData({ slug: activeSlug, lang: uiLang }, { ...page, blocks, lang: uiLang });
    await savePageMutation.mutateAsync({ slug: activeSlug, title: page.title, blocks, lang: uiLang });
    savedInSessionRef.current = true;
    return true;
  };

  const updateDraft = (fn: (blocks: LandingBlock[]) => LandingBlock[]) =>
    setDraft((d) => {
      const base = d && d.key === draftKey ? d.blocks : (page?.blocks ?? []);
      return { key: draftKey, blocks: fn(base) };
    });

  const saveDraft = () => {
    if (!page || !draftBlocks) return;
    saveBlocks(draftBlocks);
  };
  const discardDraft = () => setDraftBlocks(page?.blocks ? page.blocks.map((b) => ({ ...b })) : []);
  const finishEditing = async () => {
    const slug = activeSlug;
    const lang = uiLang;
    let saved = savedInSessionRef.current;
    if (dirty && draftBlocks) {
      // Si ESTE guardado falla no se traduce nada: traducir lo anterior pisaría
      // ES/PT con contenido viejo mientras el toast de error dice otra cosa (review).
      try { saved = await saveBlocksAsync(draftBlocks); } catch { saved = false; }
    }
    setEditing(false);
    savedInSessionRef.current = false;
    if (saved) triggerAutoTranslate(slug, lang);
  };

  // Cambio de página/idioma con el editor abierto: lo pendiente se guarda en SU
  // página (igual que "Listo") y se sale de edición — nunca viaja a la nueva.
  // Mutación propia: el aviso de éxito recién sale cuando el backend confirmó,
  // y si falla se vuelve a esa página con el borrador intacto para reintentar
  // (hallazgo del code-reviewer: antes el borrador se descartaba antes de saber).
  const leaveSaveMutation = trpc.peopleLanding.savePage.useMutation();
  const prevKeyRef = useRef(draftKey);
  useEffect(() => {
    if (prevKeyRef.current === draftKey) return;
    const prevKey = prevKeyRef.current;
    prevKeyRef.current = draftKey;
    if (!editing) return;
    if (draft && draft.key === prevKey) {
      const at = prevKey.lastIndexOf('@');
      const prevSlug = prevKey.slice(0, at);
      const prevLang = prevKey.slice(at + 1) as LandingLang;
      const prevPage = utils.peopleLanding.getPage.getData({ slug: prevSlug, lang: prevLang });
      const prevDirty = JSON.stringify(draft.blocks) !== JSON.stringify(prevPage?.blocks ?? []);
      if (prevPage && prevDirty) {
        const blocks = draft.blocks;
        leaveSaveMutation
          .mutateAsync({ slug: prevSlug, title: prevPage.title, blocks, lang: prevLang })
          .then(() => {
            utils.peopleLanding.getPage.invalidate({ slug: prevSlug, lang: prevLang });
            triggerAutoTranslate(prevSlug, prevLang);
            toast.success(
              t('people_landing.layout.saved_on_leave', 'Guardamos tus cambios en «{{page}}»', { page: prevPage.title })
            );
          })
          .catch((e: unknown) => {
            // Volver a la página con el borrador tal cual estaba, en modo edición
            setDraft({ key: prevKey, blocks });
            setEditing(true);
            navigate(prevSlug === HOME_SLUG ? '/people/landing' : `/people/landing/${prevSlug}`);
            toast.error(
              t('people_landing.layout.save_on_leave_failed', 'No pudimos guardar los cambios en «{{page}}»: {{error}}. Volvimos ahí para que reintentes.', {
                page: prevPage.title,
                error: e instanceof Error ? e.message : String(e),
              })
            );
          });
      }
    }
    setDraft(null);
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  const removeBlock = (block: LandingBlock) => {
    updateDraft((blocks) => blocks.filter((b) => b.id !== block.id));
    setDeleteTarget(null);
  };

  const setSpan = (block: LandingBlock, span: 1 | 2 | 3 | 4) =>
    updateDraft((blocks) =>
      blocks.map((b) => (b.id === block.id ? { ...b, span: span === 4 ? undefined : span } : b))
    );

  const duplicateBlock = (block: LandingBlock) =>
    updateDraft((blocks) => {
      const idx = blocks.findIndex((b) => b.id === block.id);
      const copy = JSON.parse(JSON.stringify(block)) as LandingBlock;
      copy.id = uuidv4();
      if ('items' in copy) copy.items = copy.items.map((it: any) => ({ ...it, id: uuidv4() }));
      const next = [...blocks];
      next.splice(idx + 1, 0, copy);
      return next;
    });

  const handleEditorSave = (block: LandingBlock) => {
    if (!page) return;
    updateDraft((blocks) => {
      const exists = blocks.some((b) => b.id === block.id);
      return exists ? blocks.map((b) => (b.id === block.id ? block : b)) : [...blocks, block];
    });
    setEditorState(null);
  };

  const addDivider = () => updateDraft((blocks) => [...blocks, { id: uuidv4(), type: 'divider' }]);

  const blockTypeLabel = (type: LandingBlockType) =>
    ({
      hero: t('people_landing.blocks.hero', 'Portada'),
      richtext: t('people_landing.blocks.richtext', 'Texto'),
      image: t('people_landing.blocks.image', 'Imagen'),
      cards: t('people_landing.blocks.cards', 'Tarjetas'),
      links: t('people_landing.blocks.links', 'Botones / Links'),
      embed: t('people_landing.blocks.embed', 'Video / Documento'),
      divider: t('people_landing.blocks.divider', 'Separador'),
      callout: t('people_landing.blocks.callout', 'Destacado'),
      team: t('people_landing.blocks.team', 'Tu equipo (automático)'),
      progress: t('people_landing.blocks.progress', 'Tu primer mes (automático)'),
      news: t('people_landing.blocks.news', 'Novedades (automático)'),
      video: t('people_landing.blocks.video', 'Video de bienvenida'),
      marquee: t('people_landing.blocks.marquee', 'Fotos en movimiento'),
    })[type];

  const lastEdited = useMemo(() => {
    if (!page?.updatedAt) return null;
    const locale = localeFor(uiLang);
    try {
      return new Date(page.updatedAt).toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return page.updatedAt;
    }
  }, [page?.updatedAt]);

  if (navLoading || (pageLoading && !page)) {
    return (
      <div className="flex items-center justify-center py-24">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  // Sin contenido inicial (todavía no se corrió el seed / no hay páginas)
  if (!nav || nav.pages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center space-y-4">
        <Sparkles className="h-10 w-10 text-muted-foreground" />
        <div>
          <p className="font-semibold text-lg">{t('people_landing.page.people_landing')}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {t('people_landing.empty_site', 'El landing de People todavía no tiene contenido.')}
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => createHomeMutation.mutate({ slug: HOME_SLUG, title: 'Home' })}
            disabled={createHomeMutation.isPending}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('people_landing.create_home', 'Crear página Home')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      {/* Tabs + acciones */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <nav className="flex items-center gap-1.5 overflow-x-auto flex-wrap">
          {nav.pages.map((p) => {
            const isActive = p.slug === activeSlug;
            return (
              <Link
                key={p.slug}
                to={p.slug === HOME_SLUG ? '/people/landing' : `/people/landing/${p.slug}`}
                className={cn(
                  'rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors',
                  isActive
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {p.title}
              </Link>
            );
          })}
        </nav>

        {canEdit && (
          <div className="flex items-center gap-2 shrink-0">
            {editing ? (
              <>
                {/* Qué página se está editando: los cambios van AHÍ, no a la tab que se toque después */}
                <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                  <Pencil className="h-3 w-3" />
                  {t('people_landing.layout.editing_page', 'Editando: {{page}}', { page: page?.title ?? activeSlug })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTranslateOpen(true)}
                  disabled={isTranslating}
                >
                  {isTranslating ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Languages className="mr-1.5 h-4 w-4" />
                  )}
                  {t('people_landing.translate.button', 'Traducir')}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Plus className="mr-1.5 h-4 w-4" />
                      {t('people_landing.add_section', 'Agregar sección')}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {BLOCK_TYPE_OPTIONS.map(({ type, icon: Icon }) => (
                      <DropdownMenuItem
                        key={type}
                        onClick={() =>
                          type === 'divider'
                            ? addDivider()
                            : setEditorState({ block: null, createType: type })
                        }
                      >
                        <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                        {blockTypeLabel(type)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {/* Con cambios sin guardar no se administran páginas: borrar la
                    que se está editando resucitaría una fila huérfana al salir. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagesOpen(true)}
                  disabled={dirty}
                  title={dirty ? t('people_landing.layout.save_first', 'Guardá o descartá los cambios primero') : undefined}
                >
                  <Settings2 className="mr-1.5 h-4 w-4" />
                  {t('people_landing.manage_pages', 'Páginas')}
                </Button>
                {dirty && (
                  <>
                    <Button variant="ghost" size="sm" onClick={discardDraft} title={t('people_landing.layout.discard', 'Descartar cambios')}>
                      <Undo2 className="mr-1.5 h-4 w-4" />
                      {t('people_landing.layout.discard', 'Descartar cambios')}
                    </Button>
                    <ButtonColorful variant="secondary" size="sm" hideArrow onClick={saveDraft} disabled={savePageMutation.isPending}>
                      {savePageMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      {t('people_landing.layout.save', 'Guardar cambios')}
                    </ButtonColorful>
                  </>
                )}
                <ButtonColorful size="sm" hideArrow onClick={finishEditing} disabled={savePageMutation.isPending}>
                  <Check className="h-3.5 w-3.5" />
                  {t('people_landing.done', 'Listo')}
                </ButtonColorful>
              </>
            ) : (
              <ButtonColorful variant="secondary" size="sm" hideArrow onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" />
                {t('people_landing.edit', 'Editar')}
              </ButtonColorful>
            )}
          </div>
        )}
      </div>

      {/* Aviso: se está viendo el fallback EN y al guardar se crea la variante del idioma actual */}
      {editingCreatesVariant && (
        <div className="rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-200">
          {t(
            'people_landing.lang_fallback_note',
            'Estás viendo el contenido en inglés (todavía no hay versión {{lang}}). Lo que guardes acá crea la versión {{lang}} de esta página.',
            { lang: uiLang.toUpperCase() }
          )}
        </div>
      )}

      {/* Contenido */}
      {shownBlocks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center space-y-3">
          <FileText className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t('people_landing.empty_page', 'Esta página todavía no tiene secciones.')}
          </p>
          {canEdit && !editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 h-4 w-4" />
              {t('people_landing.edit', 'Editar')}
            </Button>
          )}
        </div>
      ) : (
        <div className="pb-12 space-y-6">
          {dirty && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t('people_landing.layout.unsaved', 'Tenés cambios de layout sin guardar. Se ven en vivo; tocá "Guardar cambios" para publicarlos.')}
            </p>
          )}
          <LandingBlocksGrid
            blocks={shownBlocks}
            editing={editing}
            imageUrls={imageUrls}
            blockTypeLabel={blockTypeLabel}
            onReorder={(blocks) => setDraftBlocks(blocks)}
            onEdit={(block) => setEditorState({ block })}
            onSetSpan={setSpan}
            onDuplicate={duplicateBlock}
            onDelete={(block) => setDeleteTarget(block)}
          />

          {canEdit && lastEdited && page && (
            <p className="text-xs text-muted-foreground/70 text-right border-t pt-3">
              {t('people_landing.last_edited', 'Última edición')}: {page.updatedBy} · {lastEdited}
            </p>
          )}
        </div>
      )}

      {/* Dialogs */}
      {editorState && page && (
        <BlockEditorDialog
          open={!!editorState}
          onOpenChange={(o) => !o && setEditorState(null)}
          pageSlug={page.slug}
          block={editorState.block}
          createType={editorState.createType}
          imageUrls={imageUrls}
          onSave={handleEditorSave}
          isSaving={savePageMutation.isPending}
        />
      )}

      <PagesManagerDialog
        open={pagesOpen}
        onOpenChange={setPagesOpen}
        nav={nav.pages}
        activeSlug={activeSlug}
      />

      <AlertDialog open={translateOpen} onOpenChange={setTranslateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('people_landing.translate.title', '¿Traducir esta página?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'people_landing.translate.desc',
                'Toma la versión {{from}} (lo que estás viendo) y genera automáticamente las versiones {{targets}} con IA. Pisa el contenido actual de esos idiomas. Tarda menos de un minuto.',
                {
                  from: translateSource.toUpperCase(),
                  targets: translateTargets.map((l) => l.toUpperCase()).join(` ${t('people_landing.translate.and', 'y')} `),
                }
              )}{' '}
              {t('people_landing.translate.fixed_titles', 'El nombre de la página y el título principal quedan iguales en todos los idiomas (salvo que sean una frase).')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleTranslate}>
              {t('people_landing.translate.confirm', 'Traducir')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('people_landing.delete_block_title', '¿Eliminar sección?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'people_landing.delete_block_desc',
                'Se elimina la sección "{{type}}" de esta página.',
                { type: deleteTarget ? blockTypeLabel(deleteTarget.type) : '' }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && removeBlock(deleteTarget)}
            >
              {t('common.ui.delete', 'Eliminar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
